<template>
    <div class="container container--archive-list">

        <Loader v-if="!isReady"></Loader>

        <List v-else
            :filtering="this"
            :itemNameSingular="'document'"
            :itemNamePlural="'documents'" >
            
            <template v-slot:filters>

                <ListFilter class="filter--main">
                    <input v-model="filters.query"
                        type="text"
                        placeholder="Rechercher un document, un type, etc."/>
                </ListFilter>
                <ClientFilter v-model="filters.client_id"
                    :clients="clients"/>
                <ListFilter class="filter">    
                    <Select v-model="filters.type"
                        :options="types"
                        all="Tous les types">
                    </Select>
                </ListFilter>
                <DateFilter v-model="dates">Date document</DateFilter>
            </template>

            <template v-slot:heading>
                <div class="cell criteria">
                    <Sorter criteria="type">Type</Sorter>
                </div>
                <div class="cell cell--main criteria">
                    <Sorter criteria="title">Intitulé</Sorter>
                </div>
                <div class="cell criteria">                                
                    <Sorter criteria="contractedAt">Date document</Sorter>
                </div>
                <div class="cell criteria">                                
                    <Sorter criteria="publishedAt">Date publication</Sorter>
                </div>
                <div class="cell">
                    Nb. pièces
                </div>
                <div class="cell cell--actions">
                    Actions
                </div>
            </template>

            <div class="list__content archive-list" v-if="hasContent">

                <li class="list__content__item"
                    v-for="archive in items"
                    :key="archive.id">

                    <collapse :hidden="!hasDescription(archive)" :selected="false">

                        <div slot="collapse-header">
                            <ListItem class="list-item--card" :class="hasDescription(archive) ? '' : 'no-cursor'" 
                                :item="archive">
                                <Cell type="secondary">{{ archive.type.name }}</Cell>
                                <Cell type="main">{{ archive.title }}</Cell>
                                <Cell type="date"><Date :date="archive.contractedAt" /></Cell>
                                <Cell type="date"><Date :date="archive.publishedAt" /></Cell>
                                <Cell type="secondary">{{ archive.count }}</Cell>
                                <div class="cell cell--actions"> 
                                    <Button class="alt btn-download"
                                        :disabled="!!downloading || archive.count < 1"
                                        :loading="!!downloading && downloading.id === archive.id"
                                        @click.stop.native.prevent="download(archive)">         
                                        <span class="fa fa-download"></span>
                                    </Button>
                                </div>
                            </ListItem>
                        </div>

                        <div slot="collapse-body" v-if="hasDescription(archive)" class="archive__description"> 
                            <div v-if="archive.images.length > 0" 
                                :class="archive.images.length == 1 ? 'list-images--col1' : (archive.description ? 'list-images--col2' : '')">

                                <PreloadedImage v-for="image in archive.images"
                                    class="archive__description_image"
                                    width="100"
                                    height="86"
                                    :key="image"  
                                    :src="image" />

                            </div>                   
                            <div v-if="archive.description" v-html="archive.description" class="archive__description_html"></div>
                        </div>     

                    </collapse>                                       
                </li>

            </div>

            <div class="list__next" v-if="hasMore">
                <Loader v-if="isLoadingMore"></Loader>
                <button
                    class="button"
                    type="button"
                    @click="next"
                    v-else>Voir les archives suivantes</button>
            </div>
            
        </List>

        <div class="container_list container_list--no-result" v-if="isReady && !isFiltering && !hasContent">
            <h1 class="woocommerce-order-details__title">Aucune archive</h1>
        </div>

    </div>
</template>

<script>
import { mapState } from 'vuex'

import Button from '@/components/button/Button.vue'
import ClientFilter from '@/components/list/filters/ClientFilter.vue'
import Collapse from '@/components/collapse/Collapse.vue'
import Date from '@/components/date/Date.vue'
import DateFilter from "@/components/list/filters/DateFilter.vue"
import List, { Cell, ListFilter, ListItem, Sorter }  from '@/components/list/List.vue'
import Loader from '@/components/loader/Loader.vue'
import PreloadedImage from '../../components/image/PreloadedImage.vue'
import Select  from '@/components/select/Select.vue'

import api from '@/api/proxyWeb/archives'

import filtering from "@/containers/filtering"
import  { useClientAddresses } from '@/helpers/clientAddress'
import usePreload from '../../helpers/preload'

export default {

    name: 'ArchiveList',

    data: function() {
        return {
            filters: {
                client_id: null,
                from: null,
                to: null,
                query: '',
                type: null
            },
            downloading: null,
            archiveTypes: []
        }
    },

    computed: {

        types: function() {

            return this.archiveTypes.map((archiveType) => {
                    return {
                        value: archiveType.code,
                        name: archiveType.name
                    }
                })
        },
        
        ...mapState({

            clients: state => state.clients.all,

            clientAddresses: function(state) {
                return useClientAddresses(state.clients.all, this.filters.client_id)
            },

            markets: state => state.sites.markets

        })
    },

    methods: {

        download(archive) {
            
            if ( !! this.downloading || archive.count < 1 ) return

            this.downloading = archive
            api.getFile(archive.id)
                .then(() => this.$notify.success('Téléchargement en cours...') )
                .catch(() => this.$error( 'Impossible de télécharger les documents' ))
                .finally(() => { this.downloading = null } )
        },

        hasDescription(archive) {
            if ( archive.description || archive.images.length > 0 ) {
                return true
            } else {
                return false
            }
                
        }        
    },
  
    async created () {
        this.useFilter(api)
        this.archiveTypes = await api.getTypes()
    },

    watch: {

        items: function(items) {
            items.forEach((item) => {
                item.images.forEach( (image) => {
                    usePreload(image)
                })
            })
        }
    },

    components: {
        Button,
        Cell,
        ClientFilter,
        Collapse,
        Date,
        DateFilter,
        List,
        ListFilter,
        ListItem,
        Loader,
        PreloadedImage,
        Select,
        Sorter
    },

    mixins: [ filtering ]
}
</script>

<style scoped>

.btn-download {
    margin-left: -40px !important;
    font-size: 20px !important;
}

.archive__description {
    width: 100%; 
    display: flex;  
    flex-direction: row;
    gap: 0.5rem;
    padding: 0 0.5rem 0.5rem 0.5rem;
}
.archive__description_image {
    margin-left:5px; 
    width: 100px;
}
.archive__description_html {
    line-height: normal;
    white-space: initial;
    width: 100%;
}
.list__content__item {
    margin-bottom: 10px !important;
}
.list__content__item .no-cursor {
    cursor: default;
}
.list-images--col1 {
    width: 120px;
}
.list-images--col2 {
    width: 280px;
}

.list .list__heading .cell.cell--actions,
.list .list__content .cell.cell--actions {
    justify-content: flex-start;
}

.table {
    width: 100%;
}

.table__action {
    color: #17a2b8;
}

.table__data {}

.table__data--main {
    font-weight: 700;
}

.table__data--no-result {
    text-align: center;
    opacity: 0.8;
}

.table__row {
    cursor: pointer;
}

</style>
